// Bridges the Teamlio PF Filing extension (../../../server/teamlio extension)
// for PF (EPFO ECR) filing via window.postMessage. relay.js (a content script
// the extension injects on this origin) forwards these to the extension's
// background script and back.
// Shared with the TDS/PF twin of this file: the same check against the
// same extension, and it has to end while the click that started it still
// counts as user activation.
import { PING_ATTEMPTS, PING_TIMEOUT_MS } from './extensionPing';

const REQUEST_SOURCE = 'teamlio-pf-extension-page';
const RESPONSE_SOURCE = 'teamlio-pf-extension';

const PF_TIMEOUT_MS = 6000;

// Published Chrome Web Store listing for the Teamlio PF Filing extension
// (id bnjnabfklgnpkgggiakfieoghbommopa) — the install page to send someone
// to when pingPfExtension() comes back false. authuser/hl are per-viewer
// Google-account query params from wherever this link was copied, not part
// of the listing's identity, so they're left off here.
export const PF_EXTENSION_WEBSTORE_URL =
  'https://chromewebstore.google.com/detail/teamlio-pf-filing/bnjnabfklgnpkgggiakfieoghbommopa';

let requestCounter = 0;

function sendToExtension(type: string, payload: Record<string, unknown>, timeoutMs: number): Promise<any> {
  return new Promise((resolve, reject) => {
    const requestId = `${Date.now()}-${requestCounter++}`;

    const handler = (event: MessageEvent) => {
      if (event.source !== window) return;
      if ((event.data as any)?.source !== RESPONSE_SOURCE) return;
      if ((event.data as any)?.type !== 'response') return;
      if ((event.data as any)?.requestId !== requestId) return;
      window.removeEventListener('message', handler);
      clearTimeout(timer);
      resolve((event.data as any).response);
    };
    window.addEventListener('message', handler);

    const timer = setTimeout(() => {
      window.removeEventListener('message', handler);
      reject(new Error('extension_timeout'));
    }, timeoutMs);

    window.postMessage({ source: REQUEST_SOURCE, requestId, type, ...payload }, window.location.origin);
  });
}

export async function pingPfExtension(): Promise<boolean> {
  for (let attempt = 0; attempt < PING_ATTEMPTS; attempt += 1) {
    try {
      const response = await sendToExtension('ping', {}, PING_TIMEOUT_MS);
      if (response?.ok) return true;
    } catch {
      // timed out — retry once
    }
  }
  return false;
}

/**
 * login   — sign in, clear the portal's popups, hand the tab over
 * file    — step 2: upload the ECR return
 * approve       — step 3, read-only: open the portal's returns table and
 *                 stop, so the user can look before anything is approved
 * approveSubmit — step 3, acting: the same walk, then click Approve on the
 *                 portal, record the status it produces, and come back
 * challan       — step 4: the challan screens (still being mapped)
 *
 * ...and the ESI side, same machinery on the ESIC portal:
 * esiLogin      — sign in to the ESIC employer portal and hand it over
 * esiUpload     — the Monthly Contribution upload (still being mapped)
 */
export type PfRunMode = 'login' | 'file' | 'approve' | 'approveSubmit' | 'challan'
  | 'esiLogin' | 'esiUpload';

export interface PfFileParams {
  username: string;
  password: string;
  establishmentId?: string;
  // The caller's own session access token — the extension's background
  // service worker uses this as a Bearer token when it fetches the ECR text
  // from hrms_prod at upload time (see startFilePf's caller). Never stored
  // by the extension beyond one filing run.
  authToken?: string;
  // hrpayroll's own API origin (REACT_APP_API_BASE_URL) — lets the
  // extension's background service worker fetch the ECR text from the SAME
  // backend this session is actually talking to, instead of a hardcoded
  // default that breaks local/QA testing. Validated against an allowlist in
  // background.js, never trusted blindly.
  apiBaseUrl?: string;
  fields?: Record<string, unknown>;
  // A file for the extension to attach on the portal. Base64 because it
  // travels through window.postMessage and chrome.runtime, neither of which
  // carries a Blob. Held for the run only — never written to disk.
  upload?: { fileName: string; dataBase64: string };
  // Where the extension stops — one per step of the Statutory tab's cycle.
  // See background.js's PF_MODES for the authoritative list.
  mode?: PfRunMode;
}

// Opens the EPFO employer portal in a foreground tab (user solves the
// CAPTCHA), same as startFileGstr1. The state machine on the portal side
// (pf-extract.js) is a discovery-pass skeleton right now — see its TODOs.
export async function startFilePf({ username, password, establishmentId, authToken, apiBaseUrl, fields, mode, upload }: PfFileParams) {
  const response = await sendToExtension('pfFileStart', { username, password, establishmentId, authToken, apiBaseUrl, fields, mode, upload }, PF_TIMEOUT_MS);
  if (!response?.ok) {
    throw new Error(response?.error || 'pf_file_failed');
  }
  return response;
}

// Just sign in: opens the portal, fills the saved credentials, waits for the
// user's CAPTCHA, then clears the home page's popups and stops there. Worth
// having on its own because it is the half of a filing run that can be
// checked in isolation — if the credentials or the popup sweep are wrong,
// this says so without a wage month or an ECR anywhere near it.
export async function startPfLogin(params: Omit<PfFileParams, 'mode' | 'fields'>) {
  return startFilePf({ ...params, mode: 'login' });
}

// What pf-extract.js logged while driving the EPFO portal. It logs to the
// portal tab's console, which is a tab the extension opened and nobody is
// watching — this is the copy the background keeps, cleared at the start of
// each run (pfFileStart). One log is shared with the TDS flow, so this
// returns whichever run was last: PF filing, a TDS challan, or a 24Q upload.
export interface PfDebugLine {
  at: string;
  line: string;
}

export async function getPfDebugLog(): Promise<PfDebugLine[]> {
  const response = await sendToExtension('getDebugLog', {}, PF_TIMEOUT_MS);
  if (!response?.ok) {
    throw new Error(response?.error || 'debug_log_failed');
  }
  return response.lines || [];
}

// The installed build's version, for the "answered but has no debug log"
// case — the usual cause is the browser running a Web Store copy while the
// edits go into the local unpacked folder, and naming the version that
// answered is what makes that obvious.
export async function getPfExtensionVersion(): Promise<string | null> {
  try {
    const response = await sendToExtension('ping', {}, PING_TIMEOUT_MS);
    return response?.ok ? response.version || null : null;
  } catch {
    return null;
  }
}

// Whether a portal run is actually live, straight from the extension. The
// page uses this to free its buttons up instead of waiting for a pfError
// that may never arrive — see the poll in PayrollReports.
export async function getPfRunState(): Promise<{ running: boolean; mode: PfRunMode | null } | null> {
  try {
    const response = await sendToExtension('pfRunState', {}, PING_TIMEOUT_MS);
    if (!response?.ok) return null;
    return { running: !!response.running, mode: response.mode || null };
  } catch {
    // Nothing answered. That is not "a run is going" — an extension that
    // cannot be reached cannot be driving a portal either.
    return { running: false, mode: null };
  }
}

function onExtensionMessage(type: string, callback: (data: any) => void) {
  const handler = (event: MessageEvent) => {
    if (event.source !== window) return;
    if ((event.data as any)?.source !== RESPONSE_SOURCE) return;
    if ((event.data as any)?.type !== type) return;
    callback(event.data);
  };
  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
}

// Step status while the extension drives the portal: callback gets { step, message }.
export function onPfStatus(callback: (step: string, message: string) => void) {
  return onExtensionMessage('pfStatus', (data) => callback(data.step, data.message));
}

// Reached a point where the user needs to take over: callback gets { message }.
export function onPfReady(callback: (message: string) => void) {
  return onExtensionMessage('pfReady', (data) => callback(data.message));
}

// Flow finished: callback gets { ok, data }.
export function onPfDone(callback: (ok: boolean, data: unknown) => void) {
  return onExtensionMessage('pfDone', (data) => callback(data.ok, data.data));
}

// Flow hit a fatal error: callback gets { error }.
export function onPfError(callback: (error: string) => void) {
  return onExtensionMessage('pfError', (data) => callback(data.error));
}
